import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { membershipService } from '../../services/membershipService';
import { Subscription } from '../../types/membership';

interface CashPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  subscription: Subscription | null;
  onSuccess: () => void;
}

const CashPaymentDialog: React.FC<CashPaymentDialogProps> = ({
  open,
  onClose,
  subscription,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    amount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: 'CASH' as 'CASH' | 'CARD' | 'UPI' | 'BANK_TRANSFER',
    receiptNumber: '',
    notes: '',
  });

  React.useEffect(() => {
    if (subscription && open) {
      setFormData(prev => ({
        ...prev,
        amount: subscription.membershipPlan.price,
        receiptNumber: `RCP-${Date.now()}`,
      }));
    }
  }, [subscription, open]);

  const recordPaymentMutation = useMutation({
    mutationFn: membershipService.recordCashPayment,
    onSuccess: () => {
      onSuccess();
      onClose();
      resetForm();
    },
  });

  const resetForm = () => {
    setFormData({
      amount: 0,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMode: 'CASH',
      receiptNumber: '',
      notes: '',
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (subscription) {
      recordPaymentMutation.mutate({
        subscriptionId: subscription.id,
        ...formData,
      });
    }
  };

  const handleInputChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'amount' ? Number(value) : value,
    }));
  };

  if (!subscription) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record Cash Payment</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Subscription Details
            </Typography>
            <Typography variant="body2">
              <strong>Customer:</strong> {subscription.customer.name}
            </Typography>
            <Typography variant="body2">
              <strong>Plan:</strong> {subscription.membershipPlan.name}
            </Typography>
            <Typography variant="body2">
              <strong>Plan Price:</strong> ₹{subscription.membershipPlan.price}
            </Typography>
            <Typography variant="body2">
              <strong>Status:</strong> {subscription.status}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={handleInputChange('amount')}
                required
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Payment Date"
                type="date"
                value={formData.paymentDate}
                onChange={handleInputChange('paymentDate')}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Payment Mode</InputLabel>
                <Select
                  value={formData.paymentMode}
                  onChange={handleInputChange('paymentMode')}
                  label="Payment Mode"
                >
                  <MenuItem value="CASH">Cash</MenuItem>
                  <MenuItem value="CARD">Card</MenuItem>
                  <MenuItem value="UPI">UPI</MenuItem>
                  <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Receipt Number"
                value={formData.receiptNumber}
                onChange={handleInputChange('receiptNumber')}
                required
                placeholder="e.g., RCP-001"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleInputChange('notes')}
                placeholder="Additional notes about the payment"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              !formData.amount ||
              !formData.paymentDate ||
              !formData.receiptNumber ||
              recordPaymentMutation.isPending
            }
          >
            {recordPaymentMutation.isPending ? 'Recording...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CashPaymentDialog;